import React, { useState, useEffect } from 'react';
import { Autocomplete as MUIAutocomplete, TextField, CircularProgress, Chip } from '@mui/material';
import { useSelector } from 'react-redux';
import TEXT from './text';

/**
 * Autocomplete FieldType Component
 * 
 * Features:
 * - Autocomplete from static options list
 * - Autocomplete from database column values
 * - Support for single and multiple selection
 * - Freesolo mode (allow custom values)
 * - Async loading from IndexedDB
 * 
 * Properties Structure:
 * {
 *   options: {
 *     label: string,                    // Field label
 *     placeholder: string,               // Placeholder text
 *     variant: 'outlined' | 'filled' | 'standard',  // TextField variant
 *     multiple: boolean,                 // Allow multiple selections
 *     freeSolo: boolean,                 // Allow custom values
 *     defaultValue: any,                 // Default value
 *     staticOptions: string[],           // Static list of options
 *     fullWidth: boolean,                // Full width
 *     required: boolean,                 // Required field
 *     
 *     // Smart autocomplete (default, automatic detection)
 *     smartAutocomplete: boolean,        // Auto-detect database/table from context (default: true)
 *     
 *     // Database column autocomplete (manual configuration)
 *     useDatabase: boolean,              // Enable database autocomplete
 *     databaseName: string,              // Name of database to query
 *     dbGroupName: string,               // DBGroup name
 *     columnName: string,                // Column name to fetch values from
 *     uniqueOnly: boolean,               // Only return unique values
 *     sortAscending: boolean,            // Sort options ascending
 *     
 *     onChange: function                 // Callback on change
 *   },
 *   constrains: {
 *     maxLength: number,                 // Max length for custom values
 *     pattern: string,                   // Regex pattern for validation
 *     minOptions: number,                // Min selections (for multiple)
 *     maxOptions: number                 // Max selections (for multiple)
 *   }
 * }
 */

const AutocompleteComponent = ({ properties, value, valueUpdaterCallback, validityUpdaterCallback, fieldContext }) => {
    const language = useSelector(state => state.App_Settings.language);
    const { options = {}, constrains = {} } = properties;

    // State management
    const [inputValue, setInputValue] = useState('');
    const [selectedValue, setSelectedValue] = useState(
        value !== undefined 
            ? value 
            : (options.multiple ? (options.defaultValue || []) : (options.defaultValue || null))
    );
    const [availableOptions, setAvailableOptions] = useState(options.staticOptions || []);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    
    // Smart autocomplete: determine if it should be enabled
    const isSmartAutocompleteEnabled = () => {
        // Disable if manual database configuration is provided
        if (options.useDatabase || options.databaseName || options.dbGroupName || options.columnName) {
            return false;
        }
        // Enable by default (can be explicitly disabled)
        return options.smartAutocomplete !== false;
    };

    // Sync with parent value changes
    useEffect(() => {
        if (value !== undefined && value !== selectedValue) {
            setSelectedValue(value);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value]);

    // Load options from database if configured
    useEffect(() => {
        const loadDatabaseOptions = async () => {
            const smartEnabled = isSmartAutocompleteEnabled();
            
            // Priority order: Manual database config > Smart autocomplete > Static options
            
            // 1. Smart Autocomplete Mode (default)
            if (smartEnabled && fieldContext) {
                console.log('[AUTOCOMPLETE] Smart mode enabled, context:', fieldContext);
                
                // Extract context information
                const { databaseName, dbGroupName, fieldName, fieldIndex, allFields } = fieldContext;
                
                if (!databaseName || fieldIndex === undefined) {
                    console.warn('[AUTOCOMPLETE] Smart mode: insufficient context');
                    setAvailableOptions(options.staticOptions || []);
                    return;
                }
                
                setLoading(true);
                setError(null);
                
                try {
                    const dbRequest = indexedDB.open('ShepherdDB');
                    
                    dbRequest.onerror = (event) => {
                        console.error('[AUTOCOMPLETE] Failed to open IndexedDB:', event);
                        setError('Failed to open IndexedDB');
                        setLoading(false);
                    };
                    
                    dbRequest.onsuccess = (event) => {
                        const db = event.target.result;
                        const transaction = db.transaction(['Databases'], 'readonly');
                        const objectStore = transaction.objectStore('Databases');
                        const getAllRequest = objectStore.getAll();
                        
                        getAllRequest.onsuccess = () => {
                            const databases = getAllRequest.result;
                            
                            // Find the target database
                            let targetDatabase = databases.find(db => db.name === databaseName);
                            
                            if (dbGroupName && targetDatabase) {
                                targetDatabase = databases.find(
                                    db => db.name === databaseName && db.dbGroupName === dbGroupName
                                );
                            }
                            
                            if (!targetDatabase || !targetDatabase.records || targetDatabase.records.length === 0) {
                                console.warn('[AUTOCOMPLETE] Smart mode: no records found');
                                setAvailableOptions([]);
                                setLoading(false);
                                return;
                            }
                            
                            // Get the field at this position (fieldIndex) from DBGroup schema
                            let columnKey;
                            
                            if (allFields && allFields[fieldIndex]) {
                                columnKey = allFields[fieldIndex].name;
                            } else if (fieldName) {
                                columnKey = fieldName;
                            }
                            
                            if (!columnKey) {
                                console.warn('[AUTOCOMPLETE] Smart mode: could not determine column key');
                                setAvailableOptions([]);
                                setLoading(false);
                                return;
                            }
                            
                            console.log('[AUTOCOMPLETE] Smart mode: using column key:', columnKey);
                            
                            // Extract column values
                            const columnValues = targetDatabase.records
                                .map(record => record[columnKey])
                                .filter(val => val !== undefined && val !== null && val !== '');
                            
                            // Remove duplicates (smart mode always uses unique values)
                            let processedOptions = [...new Set(columnValues)];
                            
                            // Sort by default in smart mode
                            processedOptions = processedOptions.sort((a, b) => {
                                if (typeof a === 'string' && typeof b === 'string') {
                                    return a.localeCompare(b);
                                }
                                return a > b ? 1 : -1;
                            });
                            
                            setAvailableOptions(processedOptions);
                            setLoading(false);
                            console.log('[AUTOCOMPLETE] Smart mode: loaded', processedOptions.length, 'options');
                        };
                        
                        getAllRequest.onerror = () => {
                            console.error('[AUTOCOMPLETE] Smart mode: failed to retrieve records');
                            setLoading(false);
                        };
                        
                        transaction.oncomplete = () => {
                            db.close();
                        };
                    };
                } catch (err) {
                    console.error('[AUTOCOMPLETE] Smart mode error:', err);
                    setError(err.message);
                    setLoading(false);
                }
                
                return;
            }
            
            // 2. Manual Database Configuration Mode
            if (options.useDatabase) {
                // Validate database configuration
                if (!options.databaseName || !options.columnName) {
                    setError('Database configuration incomplete: databaseName and columnName required');
                    console.error('[AUTOCOMPLETE] Missing database configuration:', { 
                        databaseName: options.databaseName, 
                        columnName: options.columnName 
                    });
                    return;
                }
                
                console.log('[AUTOCOMPLETE] Manual database mode');
            } else {
                // 3. Static Options Mode
                setAvailableOptions(options.staticOptions || []);
                return;
            }

            setLoading(true);
            setError(null);

            try {
                const dbRequest = indexedDB.open('ShepherdDB');

                dbRequest.onerror = (event) => {
                    console.error('[AUTOCOMPLETE] Failed to open IndexedDB:', event);
                    setError('Failed to open IndexedDB');
                    setLoading(false);
                };

                dbRequest.onsuccess = (event) => {
                    const db = event.target.result;

                    // Get the database records
                    const transaction = db.transaction(['Databases'], 'readonly');
                    const objectStore = transaction.objectStore('Databases');
                    const getAllRequest = objectStore.getAll();

                    getAllRequest.onsuccess = () => {
                        const databases = getAllRequest.result;
                        
                        // Find the target database
                        let targetDatabase = databases.find(db => db.name === options.databaseName);
                        
                        // If dbGroupName is provided, also match by group
                        if (options.dbGroupName && targetDatabase) {
                            targetDatabase = databases.find(
                                db => db.name === options.databaseName && db.dbGroupName === options.dbGroupName
                            );
                        }

                        if (!targetDatabase) {
                            throw new Error(`Database "${options.databaseName}" not found`);
                        }

                        if (!targetDatabase.records || targetDatabase.records.length === 0) {
                            console.warn('[AUTOCOMPLETE] Database has no records');
                            setAvailableOptions([]);
                            setLoading(false);
                            return;
                        }

                        // Extract column values
                        const columnValues = targetDatabase.records
                            .map(record => record[options.columnName])
                            .filter(val => val !== undefined && val !== null && val !== '');

                        // Remove duplicates if uniqueOnly is true
                        let processedOptions = options.uniqueOnly 
                            ? [...new Set(columnValues)] 
                            : columnValues;

                        // Sort if requested
                        if (options.sortAscending) {
                            processedOptions = processedOptions.sort((a, b) => {
                                if (typeof a === 'string' && typeof b === 'string') {
                                    return a.localeCompare(b);
                                }
                                return a > b ? 1 : -1;
                            });
                        }

                        setAvailableOptions(processedOptions);
                        setLoading(false);
                        console.log('[AUTOCOMPLETE] Loaded options from database:', processedOptions.length, 'items');
                    };

                    getAllRequest.onerror = () => {
                        throw new Error('Failed to retrieve database records');
                    };

                    transaction.oncomplete = () => {
                        db.close();
                    };
                };
            } catch (err) {
                console.error('[AUTOCOMPLETE] Error loading database options:', err);
                setError(err.message);
                setLoading(false);
            }
        };

        loadDatabaseOptions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [options.useDatabase, options.databaseName, options.dbGroupName, options.columnName, options.uniqueOnly, options.sortAscending, options.staticOptions, options.smartAutocomplete, fieldContext]);

    // Handle value change
    const handleChange = (event, newValue) => {
        // Validate constraints for multiple selection
        if (options.multiple && Array.isArray(newValue)) {
            if (constrains.minOptions && newValue.length < constrains.minOptions) {
                console.warn(`[AUTOCOMPLETE] Minimum ${constrains.minOptions} options required`);
                return;
            }
            if (constrains.maxOptions && newValue.length > constrains.maxOptions) {
                console.warn(`[AUTOCOMPLETE] Maximum ${constrains.maxOptions} options allowed`);
                return;
            }
        }

        // Validate pattern constraint for custom values
        if (constrains.pattern && typeof newValue === 'string') {
            if (!new RegExp(constrains.pattern).test(newValue)) {
                console.warn('[AUTOCOMPLETE] Value does not match required pattern');
                return;
            }
        }

        // Validate max length
        if (constrains.maxLength && typeof newValue === 'string') {
            if (newValue.length > constrains.maxLength) {
                console.warn(`[AUTOCOMPLETE] Value exceeds maximum length of ${constrains.maxLength}`);
                return;
            }
        }

        setSelectedValue(newValue);

        // Notify parent
        if (valueUpdaterCallback) {
            valueUpdaterCallback(newValue);
        }

        // Update validity
        if (validityUpdaterCallback) {
            let isValid = true;
            
            if (options.required) {
                if (options.multiple) {
                    isValid = Array.isArray(newValue) && newValue.length > 0;
                } else {
                    isValid = newValue !== null && newValue !== '';
                }
            }

            validityUpdaterCallback(isValid);
        }

        // Custom onChange callback
        if (options.onChange) {
            options.onChange(newValue);
        }
    };

    // Handle input change (for freeSolo mode)
    const handleInputChange = (event, newInputValue) => {
        setInputValue(newInputValue);
    };

    // Handle blur event - commit inputValue when user clicks away
    const handleBlur = () => {
        // In freeSolo mode, if there's an inputValue that hasn't been committed, save it
        if (options.freeSolo && inputValue && inputValue !== selectedValue) {
            // For multiple mode, don't auto-commit on blur (user needs to press Enter or click option)
            if (!options.multiple) {
                handleChange(null, inputValue);
            }
        }
    };

    return (
        <MUIAutocomplete
            value={selectedValue}
            onChange={handleChange}
            inputValue={inputValue}
            onInputChange={handleInputChange}
            onBlur={handleBlur}
            options={availableOptions}
            loading={loading}
            multiple={options.multiple || false}
            freeSolo={options.freeSolo || false}
            disableClearable={options.disableClearable || false}
            disabled={options.disabled || false}
            fullWidth={options.fullWidth !== false}
            clearOnBlur={!options.freeSolo}
            selectOnFocus={options.freeSolo}
            handleHomeEndKeys={options.freeSolo}
            renderInput={(params) => (
                <TextField
                    {...params}
                    label={options.label || TEXT.Autocomplete?.[language] || 'Autocomplete'}
                    placeholder={options.placeholder || ''}
                    variant={options.variant || 'outlined'}
                    required={options.required || false}
                    error={!!error}
                    helperText={error || options.helperText || ''}
                    InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                            <>
                                {loading ? <CircularProgress color="inherit" size={20} /> : null}
                                {params.InputProps.endAdornment}
                            </>
                        ),
                    }}
                    sx={{ width: '100%', maxWidth: '100%' }}
                />
            )}
            renderTags={
                options.multiple
                    ? (value, getTagProps) =>
                          value.map((option, index) => (
                              <Chip
                                  label={option}
                                  {...getTagProps({ index })}
                                  key={index}
                              />
                          ))
                    : undefined
            }
            isOptionEqualToValue={(option, value) => {
                // Handle different types of comparisons
                if (typeof option === 'object' && typeof value === 'object') {
                    return JSON.stringify(option) === JSON.stringify(value);
                }
                return option === value;
            }}
            sx={{ width: '100%', maxWidth: '100%' }}
        />
    );
};

export default AutocompleteComponent;
